import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, ArrowUp, ArrowUpDown, MessageSquare } from "lucide-react";
import { api } from "../lib/api";
import { formatUSD } from "../lib/format";
import { ACTION_META, fmtDateFr } from "../lib/portfolioMeta";

const ACTION_ICON = { DEPOSIT: ArrowDown, WITHDRAW: ArrowUp, SWAP: ArrowUpDown };
const PAGE = 6;

const fmtQty = (v) =>
  v === null || v === undefined ? "" : Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 6 });

/**
 * Encart « Pourquoi ce mouvement ? » : n'apparaît que si le canal privé existe
 * (canal_available dans /config). Lien direct seulement pour les abonnés Max (canalUrl).
 */
function CanalBox({ canalUrl, maxPrice }) {
  const { data: cfg } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get("/config")).data,
  });
  if (!cfg?.canal_available) return null;
  return (
    <div className="mt-4 rounded-lg border border-violet-500/30 bg-violet-500/[0.06] p-4 flex gap-3" data-testid="sideb-canal-box">
      <span className="w-8 h-8 rounded-full bg-violet-500/15 border border-violet-500/40 grid place-items-center text-violet-300 shrink-0">
        <MessageSquare className="w-4 h-4" />
      </span>
      <div className="text-sm">
        <div className="font-medium text-zinc-100">Pourquoi ce mouvement ?</div>
        <p className="mt-0.5 text-zinc-400">
          {canalUrl
            ? "Le raisonnement derrière chaque mouvement est détaillé dans la communauté WhatsApp privée Side B."
            : "Le raisonnement derrière chaque dépôt, retrait et échange est détaillé dans la communauté WhatsApp privée Side B, incluse dans l'offre Max."}
        </p>
        {canalUrl ? (
          <a
            href={canalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-violet-300 hover:text-violet-200 font-medium"
          >
            Rejoindre la communauté WhatsApp <ArrowRight className="w-3.5 h-3.5" />
          </a>
        ) : (
          <Link to="/upgrade" className="mt-2 inline-flex items-center gap-1.5 text-violet-300 hover:text-violet-200 font-medium">
            Découvrir l'offre Max{maxPrice ? ` · ${maxPrice}` : ""} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * Derniers mouvements du portefeuille.
 * - Lecteurs : `events` (events_public du serveur) → date, type et actifs uniquement, sans montants.
 * - Admin (adminView) : historique complet avec quantités et montants, via la route réservée admin.
 */
export default function PortfolioMovements({ portfolioId, events = [], adminView = false, canalUrl = null, maxPrice = null }) {
  const [type, setType] = useState("ALL");
  const [showAll, setShowAll] = useState(false);

  const { data: adminTxs = [] } = useQuery({
    queryKey: ["sideb-admin-txs-detail", portfolioId],
    queryFn: async () => (await api.get(`/sideb/portfolios/${portfolioId}/transactions`)).data,
    enabled: adminView && !!portfolioId,
  });

  // Liste unifiée, du plus récent au plus ancien
  const rows = useMemo(() => {
    if (adminView) {
      return adminTxs.map((t) => ({
        key: t.id,
        date: t.date,
        time: t.time,
        action: (t.action || "").toUpperCase(),
        symbol: t.symbol,
        counter: t.counter_symbol,
        amount:
          t.action === "SWAP" && t.counter_symbol
            ? `${fmtQty(t.quantity)} ${t.symbol} → ${fmtQty(t.counter_quantity)} ${t.counter_symbol}`
            : `${fmtQty(t.quantity)} ${t.symbol} · ${formatUSD(t.total)}`,
      }));
    }
    return [...events].reverse().map((e, i) => ({
      key: `${e.date}-${e.time}-${i}`,
      date: e.date,
      time: e.time,
      action: e.action,
      symbol: e.symbol,
      counter: e.counter_symbol,
    }));
  }, [adminView, adminTxs, events]);

  const filtered = rows.filter((r) => type === "ALL" || r.action === type);
  const shown = showAll ? filtered : filtered.slice(0, PAGE);

  return (
    <div className="rounded-xl border border-white/5 bg-card p-6" data-testid="sideb-history-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="font-display font-bold text-lg">Derniers mouvements</h2>
        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setShowAll(false);
          }}
          className="h-8 px-2 rounded-md bg-white/[0.03] border border-white/10 text-xs text-zinc-300"
          data-testid="sideb-history-filter-action"
        >
          <option value="ALL">Tous</option>
          {Object.entries(ACTION_META).map(([id, m]) => (
            <option key={id} value={id}>
              {m.plural}
            </option>
          ))}
        </select>
      </div>

      {shown.length === 0 ? (
        <p className="py-8 text-sm text-zinc-500 text-center">
          {rows.length === 0 ? "Aucun mouvement pour l'instant." : "Aucun mouvement de ce type."}
        </p>
      ) : (
        <div data-testid="sideb-history-list">
          {shown.map((r) => {
            const meta = ACTION_META[r.action] || { label: r.action, cls: "text-zinc-300 bg-white/5 border-white/10" };
            const Icon = ACTION_ICON[r.action] || ArrowUpDown;
            return (
              <div key={r.key} className="flex items-center gap-3 py-3 border-t border-white/5 first:border-0" data-testid="sideb-history-row">
                <span className={`w-8 h-8 rounded-full border grid place-items-center shrink-0 ${meta.cls}`}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100">
                    {meta.label}{" "}
                    <span className="font-mono-num">{r.action === "SWAP" && r.counter ? `${r.symbol} → ${r.counter}` : r.symbol}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    {fmtDateFr(r.date)}
                    {r.time ? ` · ${String(r.time).slice(0, 5)}` : ""}
                  </div>
                </div>
                {adminView && r.amount && (
                  <span className="ml-auto text-right font-mono-num text-xs text-zinc-400">{r.amount}</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {filtered.length > PAGE && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-3 w-full text-center text-sm text-emerald-400 hover:text-emerald-300"
          data-testid="sideb-history-more"
        >
          Voir tout l'historique ({filtered.length})
        </button>
      )}

      {!adminView && rows.length > 0 && (
        <p className="mt-3 text-[11px] text-zinc-600">
          Dates et actifs uniquement : les quantités et montants ne sont pas publiés.
        </p>
      )}

      <CanalBox canalUrl={canalUrl} maxPrice={maxPrice} />
    </div>
  );
}
